import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/content';

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => {
    setIndex((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const prev = useCallback(() => {
    setIndex((current) => (current - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setInterval(next, 6500);
    return () => window.clearInterval(timer);
  }, [next, paused]);

  const slide = heroSlides[index];

  return (
    <section
      className="relative w-full overflow-hidden bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点内容推荐"
    >
      <div className="relative h-[440px] w-full md:h-[560px] lg:h-[600px]">
        {heroSlides.map((item, slideIndex) => (
          <img
            key={item.id}
            src={item.image}
            alt={`${item.title} 焦点图`}
            title={`${item.title} 影视焦点推荐`}
            className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-700 ${
              slideIndex === index ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/92 via-foreground-950/60 to-foreground-950/20"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/25"></div>

        <div className="relative z-10 flex h-full w-full flex-col justify-end px-4 pb-16 md:justify-center md:px-8 md:pb-0 lg:px-14">
          <div key={slide.id} className="animate-fade-up max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/95 px-3 py-1 font-label text-[11px] font-bold uppercase tracking-[0.14em] text-foreground-950">
              <i className="ri-fire-fill text-[12px]"></i>
              {slide.eyebrow}
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold leading-tight tracking-tight text-background-50 md:text-5xl lg:text-[56px]">
              {slide.title}
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
              {slide.description}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 font-label text-xs text-background-200/80">
              <span>{slide.meta}</span>
              <span className="flex items-center gap-1 text-accent-300">
                <i className="ri-star-fill text-[11px]"></i>
                {slide.rating}
              </span>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${slide.id}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-circle-line text-[17px]"></i>
                查看详情
              </Link>
              <Link
                to="/movies"
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/35 bg-background-50/10 px-5 py-2.5 text-sm text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
              >
                浏览全部影片
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 right-4 z-20 flex items-center gap-3 md:bottom-8 md:right-8">
          <div className="flex items-center gap-1.5">
            {heroSlides.map((item, dotIndex) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setIndex(dotIndex)}
                aria-label={`切换到第 ${dotIndex + 1} 张焦点图`}
                className={`h-1.5 cursor-pointer rounded-full transition-all ${
                  dotIndex === index ? 'w-7 bg-accent-500' : 'w-3.5 bg-background-50/45 hover:bg-background-50/80'
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={prev}
              aria-label="上一张"
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/15"
            >
              <i className="ri-arrow-left-s-line text-[17px]"></i>
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="下一张"
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/15"
            >
              <i className="ri-arrow-right-s-line text-[17px]"></i>
            </button>
          </div>
        </div>

        <div className="absolute bottom-5 left-4 z-20 hidden font-label text-[11px] tracking-[0.2em] text-background-200/70 md:left-8 md:block">
          {String(index + 1).padStart(2, '0')} / {String(heroSlides.length).padStart(2, '0')}
        </div>
      </div>
    </section>
  );
}